import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createLayout,
  deleteLayout,
  fetchLayouts,
  setActiveLayout,
  updateLayout,
  type LayoutsResponse,
} from '@apis/preferences';
import {
  DEFAULT_LAYOUT,
  DEFAULT_LAYOUT_ID,
  mergeWithDefault,
  type CardId,
  type DashboardLayoutItem,
} from './layout.defaults';

const QUERY_KEY = ['dashboard-layouts'] as const;

function resolveLayout(data: LayoutsResponse | undefined, activeId: string): DashboardLayoutItem[] {
  if (!data || activeId === DEFAULT_LAYOUT_ID) return DEFAULT_LAYOUT;
  const found = data.layouts.find((l) => l.id === activeId);
  return found ? mergeWithDefault(found.items) : DEFAULT_LAYOUT;
}

export function useDashboardLayout() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: ({ signal }) => fetchLayouts(signal) });

  const serverActiveId = query.data?.activeLayoutId ?? DEFAULT_LAYOUT_ID;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DashboardLayoutItem[] | null>(null);

  const savedLayout = useMemo(
    () => resolveLayout(query.data, serverActiveId),
    [query.data, serverActiveId],
  );
  const layout = editing && draft ? draft : savedLayout;

  const invalidate = () => qc.invalidateQueries({ queryKey: QUERY_KEY });

  const createMut = useMutation({
    mutationFn: (v: { name: string; items: DashboardLayoutItem[] }) => createLayout(v.name, v.items),
    onSuccess: () => { setEditing(false); setDraft(null); void invalidate(); },
  });
  const updateMut = useMutation({
    mutationFn: (v: { id: string; items: DashboardLayoutItem[] }) => updateLayout(v.id, { items: v.items }),
    onSuccess: () => { setEditing(false); setDraft(null); void invalidate(); },
  });
  const renameMut = useMutation({
    mutationFn: (v: { id: string; name: string }) => updateLayout(v.id, { name: v.name }),
    onSuccess: () => void invalidate(),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteLayout(id),
    onSuccess: () => void invalidate(),
  });
  const activeMut = useMutation({
    mutationFn: (id: string) => setActiveLayout(id),
    onSuccess: () => void invalidate(),
  });

  const isDefaultActive = serverActiveId === DEFAULT_LAYOUT_ID;

  // Stabilize action callbacks so DashboardLayoutControls (and any child that
  // receives them) only re-renders when its deps genuinely change — not on
  // every drag tick that updates the draft state.
  //
  // TanStack Query's `mutate` is referentially stable across renders (unlike
  // the mutation object, whose identity changes with status), so destructure
  // the mutate functions and depend on those — keeps the callbacks stable and
  // satisfies exhaustive-deps without listing the whole mutation object.
  const { mutate: createMutate } = createMut;
  const { mutate: updateMutate } = updateMut;
  const { mutate: renameMutate } = renameMut;
  const { mutate: deleteMutate } = deleteMut;
  const { mutate: activeMutate } = activeMut;

  const startEdit = useCallback(() => {
    setDraft(savedLayout);
    setEditing(true);
  }, [savedLayout]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraft(null);
  }, []);

  // The grid reports positions for the items it RENDERS, which is the visible
  // subset — hidden cards are filtered out before it ever sees them. So this
  // merges by id instead of replacing the draft: an item missing from `next` is
  // a hidden card and keeps its stored entry, flag and slot included. Replacing
  // wholesale (this used to be `setDraft` directly) dropped every hidden card
  // from the layout on the first grid tick after a toggle, which un-hid it on
  // the next save.
  const onDraftChange = useCallback(
    (next: DashboardLayoutItem[]) => {
      setDraft((current) => {
        const moved = new Map(next.map((item) => [item.i, item]));
        return (current ?? savedLayout).map((item) => moved.get(item.i) ?? item);
      });
    },
    [savedLayout],
  );

  // Card visibility is draft state exactly like position and size: it moves
  // while editing and is persisted by Save, never written on the toggle itself.
  // The updater form reads the live draft, so a burst of toggles doesn't drop
  // any of them; it falls back to the saved layout because the very first
  // toggle can land before startEdit's setDraft has committed.
  const toggleCardVisibility = useCallback(
    (id: CardId) => {
      setDraft((current) =>
        (current ?? savedLayout).map((item) =>
          item.i === id ? { ...item, hidden: !item.hidden } : item,
        ),
      );
    },
    [savedLayout],
  );

  const selectLayout = useCallback(
    (id: string) => activeMutate(id),
    [activeMutate],
  );

  // One save action, name-keyed: typing an existing saved layout's name
  // overwrites it; a new name creates a new layout. The synthetic Default is
  // not in query.data.layouts, so typing its name always creates (Default is
  // read-only) — which is the intended behaviour.
  const saveLayout = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const items = draft ?? savedLayout;
      const found = query.data?.layouts.find((l) => l.name === trimmed);
      if (found) updateMutate({ id: found.id, items });
      else createMutate({ name: trimmed, items });
    },
    [draft, savedLayout, query.data, updateMutate, createMutate],
  );

  const renameLayout = useCallback(
    (id: string, name: string) => renameMutate({ id, name }),
    [renameMutate],
  );

  const removeLayout = useCallback(
    (id: string) => deleteMutate(id),
    [deleteMutate],
  );

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    layout,
    layouts: query.data?.layouts ?? [],
    activeLayoutId: serverActiveId,
    editing,
    /** false when the active layout is the read-only Default. */
    canUpdateActive: !isDefaultActive,

    startEdit,
    cancelEdit,
    onDraftChange,
    toggleCardVisibility,

    selectLayout,
    saveLayout,
    renameLayout,
    removeLayout,

    isMutating:
      createMut.isPending || updateMut.isPending || renameMut.isPending || deleteMut.isPending || activeMut.isPending,
  };
}
